// ─────────────────────────────────────────────
//  AssignSteps — every screen of the quick-actions menu AFTER the first one:
//  Takeover, Second CRNA, Edit CRNA, Transfer, and the "who was displaced"
//  follow-up.
//
//  Lifted out of `BenchAssignFlow` when "Edit CRNA" landed and pushed that file
//  over the 500-line house rule. Nothing here decides anything: every write is
//  a callback the flow owns and guards, because the guard has to sit next to the
//  position it is guarding, not next to the button.
//
//  ⚠️ THE STEP NAMES ARE THE FLOW'S STATE MACHINE, not a local invention —
//  `choose | takeover | second_crna | edit_depart | transfer | home_reassign`.
//  Rendering nothing for an unknown step is deliberate: the flow resets to
//  "choose" on close, so a stale value can only ever mean a bug, and a blank
//  panel with Cancel still reachable is the safe way to show one.
// ─────────────────────────────────────────────
import React from "react";
import st from "./benchAssignStyles";
import ChoiceMenu from "./ChoiceMenu";
import PickStep from "./PickStep";
import EditDepartStep from "./EditDepartStep";
import { seatCellName } from "../../domain/secondProviderWrite";

export default function AssignSteps({
  step,
  provider,
  roomLabel,
  busy = false,
  // Takeover
  occupiedRows = [],
  onTakeover,
  // Second CRNA
  existingSecond = null,
  secondCandidates = [],
  onAssignSecond,
  // Edit CRNA — the leaving time the row RECORDS, "" when there is none.
  departTime = "",
  onSaveDepart,
  // Transfer
  transferTargets = [],
  labelOf = (f) => f.id,
  onTransfer,
  // After a takeover
  displaced = null,
  onSendHome,
  // Navigation
  onBack,
  onCancel,
}) {
  if (step === "takeover") {
    return (
      <PickStep
        title="Takeover — pick a room"
        sub={`${provider.name} will become the primary CRNA; the current one is displaced.`}
        menuLabel="Rooms to take over"
        emptyText="No rooms have a CRNA assigned yet."
        items={occupiedRows.map((row) => ({
          key: row.id,
          primary: row.orRoom,
          // ⚠️ THE SEAT, NOT THE RAW CELL. On a legacy row `ahpName` is still
          // "Trevor Stamand 5 orient Kayla Gibbons"; this is a list of PEOPLE to
          // displace, not of sheet cells.
          secondary: `${seatCellName(row) || row.ahpName} →`,
          onPick: () => onTakeover(row),
        }))}
        busy={busy}
        onBack={onBack}
        onCancel={onCancel}
      />
    );
  }

  // ⚠️ THE ROOM IS ALREADY KNOWN — the menu was opened from the provider who is
  // running it. Picking a person is the whole step, which is why there is no
  // room picker here and no confirmation after it: a second question would be a
  // second chance to put somebody in the wrong OR.
  if (step === "second_crna") {
    return (
      <PickStep
        title={`Second CRNA in ${roomLabel}`}
        sub={
          existingSecond
            ? `They stand with ${provider.name}, replacing ${existingSecond.name} — ${provider.name} keeps the room.`
            : `They stand with ${provider.name} — ${provider.name} keeps the room.`
        }
        menuLabel={`CRNAs who can join ${provider.name}`}
        emptyText="Nobody else is on today's roster."
        items={secondCandidates.map((p) => ({
          key: p.id,
          primary: p.name,
          secondary: `${(p.subRole || "crna").toUpperCase()}${p.shiftCode ? ` · ${p.shiftCode}` : ""}`,
          onPick: () => onAssignSecond(p),
        }))}
        busy={busy}
        onBack={onBack}
        onCancel={onCancel}
      />
    );
  }

  // ⚠️ THE ONE STEP THAT WORKS WITHOUT A STAFF ID. It writes a field on the
  // row's SLOT, so it is reachable from a name the importer never resolved —
  // which is precisely the name whose leaving time is missing. `provider.name`
  // is the seat / second name, already stripped of the shift digits, never the
  // raw two-person cell.
  if (step === "edit_depart") {
    return (
      <EditDepartStep
        who={provider.name}
        roomLabel={roomLabel}
        value={departTime}
        busy={busy}
        onSave={onSaveDepart}
        onBack={onBack}
        onCancel={onCancel}
      />
    );
  }

  if (step === "transfer") {
    return (
      <>
        <div style={st.title}>Transfer — pick the destination</div>
        <div style={st.sub}>{provider.name} leaves this facility and appears as Traveling until Arrival is pressed.</div>
        <ChoiceMenu
          label="Destination facilities"
          busy={busy}
          items={transferTargets.map((f) => ({ key: f.id, icon: "🏥", label: labelOf(f), onPick: () => onTransfer(f) }))}
        />
        <div style={st.actions}>
          <button type="button" style={st.ghost} onClick={onBack} disabled={busy}>← Back</button>
          <button type="button" style={st.ghost} onClick={onCancel} disabled={busy}>Cancel</button>
        </div>
      </>
    );
  }

  if (step === "home_reassign" && displaced) {
    return (
      <>
        <div style={st.title}>{displaced.name} was displaced</div>
        <div style={st.sub}>They're back in Unassigned. What next?</div>
        <ChoiceMenu
          label={`What next for ${displaced.name}`}
          busy={busy}
          items={[
            { key: "home", icon: "🏠", label: "Home", sub: `Send ${displaced.name} home for the day.`,
              onPick: () => onSendHome(displaced.id, displaced.name) },
            { key: "reassign", icon: "🔄", label: "Reassign", sub: "Leave them in Unassigned to place again.",
              onPick: onCancel },
          ]}
        />
      </>
    );
  }

  return null;
}
